import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiService } from '@mm-aggregator/drf-dynamics';

export interface FeeSummaryResponse {
  gross_gain: string;
  provider_cost: string;
  merchant_paid: string;
  total_fees: string;
  gross_gain_by_currency: Record<string, string>;
  provider_cost_by_currency: Record<string, string>;
  merchant_paid_by_currency: Record<string, string>;
  fee_breakdown: Array<{ fee_type: string; amount: string }>;
}

export interface FeeBreakdownResponse {
  group_by: string;
  rows: Array<{ code: string; name: string; amount: string }>;
}

export interface FeeTransactionRow {
  id: string;
  fee_type: string;
  amount: string;
  currency: string;
  operation_type: string;
  provider_code: string | null;
  financial_entity_code: string | null;
  merchant_id: string | null;
  merchant_name: string | null;
  transaction_reference: string;
  transaction_id: string;
  created_at: string;
}

export interface FeeTransactionResponse {
  rows: FeeTransactionRow[];
}

export interface FeeConfigurationRow {
  id: string;
  name: string;
  description?: string | null;
  scope: string;
  scope_rank: number;
  scope_target_label: string;
  fee_type: string;
  amount_type: string;
  value: string;
  composition_mode: string;
  operation_type: string | null;
  currency: string | null;
  currency_id: string | null;
  provider_code: string | null;
  provider_id: string | null;
  financial_entity_code: string | null;
  financial_entity_id: string | null;
  merchant_id: string | null;
  merchant_name: string | null;
  merchant_application_id: string | null;
  merchant_application_name: string | null;
  parent_rule_id: string | null;
  parent_rule_name: string | null;
  effective_from: string | null;
  effective_until: string | null;
  is_active: boolean;
}

export interface FeeConfigurationResponse {
  rows: FeeConfigurationRow[];
}

export interface FeeAnalyticsFilters {
  period?: string;
  start_date?: string;
  end_date?: string;
  currency?: string;
  operation_type?: string;
  provider_id?: string;
  financial_entity_id?: string;
  merchant_id?: string;
  fee_type?: string;
  transaction_reference?: string;
  transaction_id?: string;
}

export interface PricingRulePayload {
  name?: string;
  description?: string;
  scope: string;
  operation_type?: string | null;
  fee_type: string;
  amount_type: string;
  value: string;
  currency?: string | null;
  composition_mode: string;
  parent_rule?: string | null;
  effective_from?: string | null;
  effective_until?: string | null;
  is_active?: boolean;
  financial_entity?: string | null;
  api_provider?: string | null;
  merchant?: string | null;
  merchant_application?: string | null;
}

export interface PricingRuleResponse extends PricingRulePayload {
  id: string;
  scope_rank: number;
  scope_target_label: string;
  parent_rule_id: string | null;
  parent_rule_name: string | null;
  provider_code: string | null;
  financial_entity_code: string | null;
  merchant_name: string | null;
  merchant_application_name: string | null;
  currency_code: string | null;
  created_at: string;
  updated_at: string;
}

@Injectable({ providedIn: 'root' })
export class FeeAnalyticsService {
  private readonly api = inject(ApiService);
  private readonly basePath = 'finances/fee-analytics/';
  private readonly pricingRulesPath = 'finances/pricing-rules/';

  getSummary(filters: FeeAnalyticsFilters = {}): Observable<FeeSummaryResponse> {
    return this.api.get<FeeSummaryResponse>(`${this.basePath}summary/`, this.cleanFilters(filters));
  }

  getBreakdown(groupBy: string, filters: FeeAnalyticsFilters = {}): Observable<FeeBreakdownResponse> {
    return this.api.get<FeeBreakdownResponse>(`${this.basePath}breakdown/`, {
      ...this.cleanFilters(filters),
      group_by: groupBy,
    });
  }

  getTransactionBreakdown(filters: FeeAnalyticsFilters): Observable<FeeTransactionResponse> {
    return this.api.get<FeeTransactionResponse>(`${this.basePath}transaction/`, this.cleanFilters(filters));
  }

  getConfigurations(filters: FeeAnalyticsFilters = {}): Observable<FeeConfigurationResponse> {
    return this.api.get<FeeConfigurationResponse>(`${this.basePath}configurations/`, this.cleanFilters(filters));
  }

  getPricingRules(filters: FeeAnalyticsFilters = {}): Observable<{ results: PricingRuleResponse[] } | PricingRuleResponse[]> {
    return this.api.get<{ results: PricingRuleResponse[] } | PricingRuleResponse[]>(
      `${this.pricingRulesPath}`,
      this.cleanFilters(filters),
    );
  }

  createPricingRule(payload: PricingRulePayload): Observable<PricingRuleResponse> {
    return this.api.post<PricingRuleResponse>(`${this.pricingRulesPath}`, payload);
  }

  updatePricingRule(id: string, payload: Partial<PricingRulePayload>): Observable<PricingRuleResponse> {
    return this.api.patch<PricingRuleResponse>(`${this.pricingRulesPath}${id}/`, payload);
  }

  private cleanFilters(filters: FeeAnalyticsFilters): Record<string, string> {
    const entries = Object.entries(filters).filter(([, value]) => value !== undefined && value !== null && value !== '');
    return Object.fromEntries(entries) as Record<string, string>;
  }
}
